import { useRef } from 'react';
import type { RoomView } from '@nest-recruit/shared';
import { errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';
import { useArchiveRoom, useCreateRoom, useRenameRoom, useRestoreRoom } from '../../../interviews/api/interviews-api';

export interface RoomActions {
  /** Adds at once with Undo (which takes the new room off again). `onRefused` gets the server's words. */
  add(venture: { readonly id: string; readonly name: string }, name: string, onRefused: (message: string) => void): void;
  /** Renames at once with Undo (which renames it back). */
  rename(room: RoomView, name: string, onRefused: (message: string) => void): void;
  /** Takes it off at once with Undo (which puts it back, `POST /rooms/:id/restore`). */
  takeOff(room: RoomView): void;
}

/**
 * Rooms A (owner, 2026-11-02): add, rename and take off each happen at once and offer a 10-second Undo
 * instead of asking first (§5a rule 8). Undo pressed before the server has answered waits for that
 * answer, then reverses it. A refusal closes the Undo — there is nothing to undo — and says why.
 */
export function useRoomActions(): RoomActions {
  const create = useCreateRoom();
  const renameRoom = useRenameRoom();
  const archive = useArchiveRoom();
  const restore = useRestoreRoom();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const dismiss = useToasts((state) => state.dismiss);
  const nextKey = useRef(0);
  // Review W16: one Undo per room, the newest. An older one holds the room as it was then (its version), so after a
  // rename the add's Undo would only be refused as stale; a newer change on the same room closes the older toast.
  const undoOf = useRef(new Map<string, string>());

  const claim = (roomId: string, toastId: string): void => {
    const older = undoOf.current.get(roomId);
    if (older !== undefined && older !== toastId) dismiss(older);
    undoOf.current.set(roomId, toastId);
  };

  const refused = (message: string, error: unknown): void => {
    show({ tone: 'danger', message, detail: errorMessage(error) });
  };

  function takeOff(room: RoomView, isUndo = false): void {
    const removing = archive.mutateAsync(room);
    const toastId = isUndo
      ? null
      : showUndo(`${room.name} taken off the list`, () => {
          removing.then(() => putBack(room), () => undefined);
        });
    if (toastId) claim(room.id, toastId);
    removing.catch((error: unknown) => {
      if (toastId) dismiss(toastId);
      refused(isUndo ? `${room.name} is still on the list` : `${room.name} wasn't taken off`, error);
    });
  }

  function putBack(room: RoomView): void {
    restore.mutateAsync(room).catch((error: unknown) => refused(`${room.name} wasn't put back`, error));
  }

  function add(venture: { readonly id: string; readonly name: string }, name: string, onRefused: (message: string) => void): void {
    nextKey.current += 1;
    const adding = create.mutateAsync({ ventureId: venture.id, ventureName: venture.name, name, pendingKey: `${venture.id}:${nextKey.current}` });
    const toastId = showUndo(`${name} added`, () => {
      adding.then((room) => takeOff(room, true), () => undefined);
    });
    adding.then((room) => claim(room.id, toastId), () => undefined);
    adding.catch((error: unknown) => {
      dismiss(toastId);
      refused(`${name} wasn't added`, error);
      onRefused(errorMessage(error));
    });
  }

  function rename(room: RoomView, name: string, onRefused: (message: string) => void): void {
    const renaming = renameRoom.mutateAsync({ room, name });
    const toastId = showUndo(`${room.name} renamed to ${name}`, () => {
      renaming.then(
        (saved) => renameRoom.mutateAsync({ room: saved, name: room.name }).catch((error: unknown) => refused(`${name} wasn't renamed back`, error)),
        () => undefined,
      );
    });
    claim(room.id, toastId);
    renaming.catch((error: unknown) => {
      dismiss(toastId);
      refused(`${room.name} wasn't renamed`, error);
      onRefused(errorMessage(error));
    });
  }

  return { add, rename, takeOff: (room) => takeOff(room) };
}
